import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import * as XLSX from 'xlsx';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { RouterModule } from '@angular/router';
import { ContainerReportFilter } from '../../core/dtos/container-report-filter.dto';
import { ContainerReportData } from '../../core/models/container-report-data.model';
import { ClientService } from '../../core/services/client.service';
import { ContainerTypeService } from '../../core/services/container-type.service';
import { ReportsService } from '../../core/services/reports.service';
import { StateService } from '../../core/services/state.service';
import { ContainerOwnerService } from '../../core/services/container-owner.service';
import { MultipleSelectComponent } from "../../shared/multiple-select/multiple-select.component";
import { SavedViewsService } from '../../core/services/saved-views.service';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../../core/services/auth.service';
import { ContainerTypeDto, CreateUserFilterDto, MaintenanceRecordDetailFilterDto, MaintenanceRecordStatsDto, MaintenanceRecordStatsFilterDto, StateDto } from '../../core/models/maintenance-record.model';
import { MatIcon } from "@angular/material/icon";
import { MaintenanceRecordService } from '../../core/services/maintenance-record.service';

@Component({
  selector: 'app-saved-views-report',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatSelectModule,
    MatInputModule,
    FormsModule,
    RouterModule,
    MultipleSelectComponent,
    MatIcon,
    ReactiveFormsModule
  ],
  templateUrl: './saved-views-report.component.html',
  styleUrl: './saved-views-report.component.scss'
})
export class SavedViewsReportComponent implements OnInit {
  dataSource!: MaintenanceRecordStatsDto[]
  displayedColumns: string[] = ['containerType'];
  states: StateDto[] = [];

  minLengthFilterName: number = 5

  containerTypes: { id: number; name: string }[] = [];
  statuses: { id: number; name: string }[] = [];
  clients: { id: number; name: string }[] = [];
  owners: { id: number; name: string }[] = [];
  filter: any = {
    containerTypeIds: [],
    stateIds: [],
    clientIds: [],
    ownerIds: []
  };

  showSavedFilters: boolean = true;
  savedFiltersDataSource: any = []

  showSaveFilterModal = false
  successSaveFilter = false
  isValidFormSaveFilter = true
  newFilterName: string = ''

  filterNameForm!: FormGroup;
  showConfirmDeleteModal = false
  idFilterForDelete: number | null = null
  successDeleteFilter = false
  showErrorModal = false


  constructor(
    private containerTypeService: ContainerTypeService,
    private clientService: ClientService,
    private stateService: StateService,
    private containerOwnerService: ContainerOwnerService,
    private savedViewsService: SavedViewsService,
    private authService: AuthService,
    private fb: FormBuilder,
    private maintenanceRecordService: MaintenanceRecordService
  ) { }

  async ngOnInit(): Promise<void> {
    this.loadFilters()
    this.getSavedFiltersByUserId()

  }

  //METODOS DE CONSUMO DE API

  loadFilters(): void {
    this.containerTypeService.getContainerTypes().subscribe(types => (this.containerTypes = types));
    this.stateService.getStates().subscribe(statuses => (this.statuses = statuses));
    this.clientService.getClients().subscribe(clients => (this.clients = clients.map((item: any) => {
      return { ...item, name: item.razonSocial }
    })));
    this.containerOwnerService.getContainerOwners().subscribe(response => (this.owners = response))
  }

  async getSavedFiltersByUserId() {
    const userId = this.authService.getUserId()
    if (!userId) return console.error('no es posible obtener userId para obtener filtros guardados por usuario')
    try {
      const res = await firstValueFrom(this.savedViewsService.getByUserId(userId))
      this.savedFiltersDataSource = res.sort((a, b) => a.id - b.id);
    } catch (err) {
      console.error(err)
      this.savedFiltersDataSource = []
    }
  }

  async saveFilter(payload: CreateUserFilterDto) {

    try {
      const res = await firstValueFrom(this.savedViewsService.create(payload))
      this.successSaveFilter = true
      this.getSavedFiltersByUserId()
      this.onResetFilter()
    } catch (err) {
      console.error(err)
      this.successSaveFilter = false
    }
  }

  onResetFilter() {
    this.filter = {
      containerTypeIds: [],
      stateIds: [],
      clientIds: [],
      ownerIds: []
    };
  }

  async deleteSavedFilter(id: number) {
    try {
      const res = await firstValueFrom(this.savedViewsService.delete(id))
      this.getSavedFiltersByUserId()
      this.successDeleteFilter = true
    } catch (err) {
      console.error(err)
      this.successDeleteFilter = false
    }
  }

  async getMaintenanceRecordStats() {
    const payload: MaintenanceRecordStatsFilterDto = {
      typePackagingIds: this.filter.containerTypeIds,
      statusIds: this.filter.stateIds,
      clientIds: this.filter.clientIds,
      containerOwnerIds: this.filter.ownerIds
    }
    try {
      const res = await firstValueFrom(this.maintenanceRecordService.getStats(payload))
      this.dataSource = res
      // Obtener los estados dinámicamente desde la primera fila
      if (res.length > 0 && res[0].totals.length > 0) {
        this.states = res[0].totals.map((t: any) => t.state);
        // Agregar columnas dinámicas
        this.displayedColumns = ['containerType', ...this.states.map(s => `state_${s.id}`)];
      }
    } catch (err) {
      console.error(err)
    }
  }

  // Helper para obtener el total de un estado específico
  getTotalForState(data: MaintenanceRecordStatsDto, stateId: number): number {
    const stateTotal = data.totals.find(t => t.state.id === stateId);
    return stateTotal ? stateTotal.total : 0;
  }

  onCellClick(containerType: ContainerTypeDto, state: StateDto) {
    this.getMaintenanceRecordStatsDetails(containerType.id, state.id)
  }

  async getMaintenanceRecordStatsDetails(typePackagingId: number, statusId: number) {
    const payload: MaintenanceRecordDetailFilterDto = {
      typePackagingIds: [typePackagingId],
      statusIds: [statusId],
      clientIds: this.filter.clientIds,
      containerOwnerIds: this.filter.ownerIds
    }
    try {
      const res = await firstValueFrom(this.maintenanceRecordService.getStatsDetails(payload))
    } catch (err) {
      console.error(err)
    }
  }

  //metodo para capturar evento onCLick de aplicar filtro
  onApplyFilter(filter: any): void {
    if (!filter) return;
    this.hydrateFilters(filter);
  }

  //aplica o hidrata filtro seleccionado en UI
  private hydrateFilters(filter: any): void {
    try {
      const config = JSON.parse(filter.filterJson);

      this.filter = {
        ...this.filter,
        containerTypeIds: config.containerTypeIds || [],
        stateIds: config.stateIds || [],
        clientIds: config.clientIds || [],
        ownerIds: config.ownerIds || []
      };

    } catch (error) {
      console.error('Error al parsear el filtro guardado:', error);
      //this.snackBar.open('El filtro guardado está dañado', 'Cerrar', { duration: 3000 });
    }
  }

  isSomeFilterSelected(): boolean {
    if (this.filter.containerTypeIds.length > 0) return true
    if (this.filter.stateIds.length > 0) return true
    if (this.filter.clientIds.length > 0) return true
    if (this.filter.ownerIds.length > 0) return true
    return false
  }

  closeModals() {
    this.showSaveFilterModal = false
    this.showConfirmDeleteModal = false
    this.showErrorModal = false
  }

  openSaveFilterModal() {
    if (this.savedFiltersDataSource.length >= 10) {
      this.showErrorModal = true
      return
    }
    this.showSaveFilterModal = true
    this.successSaveFilter = false
    this.createFilterNameForm()
  }

  createFilterNameForm() {
    this.filterNameForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(this.minLengthFilterName), Validators.maxLength(100), this.uniqueNameValidator()]]
    });
  }

  uniqueNameValidator(): ValidatorFn {
    const existingNames: string[] = this.savedFiltersDataSource.map((item: any) => item.name)
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      const nameExists = existingNames.some(
        name => name.toLowerCase() === control.value.toLowerCase().trim()
      );
      return nameExists ? { duplicateName: true } : null;
    };
  }

  onSaveFilter() {
    if (this.filterNameForm.invalid) return this.filterNameForm.markAllAsTouched();
    const payload = this.obtainUserFilterDTO()
    this.saveFilter(payload)
  }

  private obtainUserFilterDTO() {
    const userId = this.authService.getUserId()
    const filterJson = JSON.stringify(this.filter)
    const name = this.filterNameForm.value.name;

    const payload: CreateUserFilterDto = {
      createdUserId: userId!,
      name,
      module: 'cde',
      filterJson,
      isFavorite: false
    }
    return payload;

  }

  openDeleteModal(item: any) {
    this.showConfirmDeleteModal = true
    this.idFilterForDelete = item.id
    this.successDeleteFilter = false
  }

  onDelete() {
    if (!this.idFilterForDelete) return
    this.deleteSavedFilter(this.idFilterForDelete)
  }

  toggleSavedFilters(): void {
    this.showSavedFilters = !this.showSavedFilters;
  }

  exportToExcel(): void {
    if (!this.dataSource || this.dataSource.length === 0) {
      console.warn('No hay datos para exportar');
      return;
    }

    // Construir los headers dinámicamente
    const headers = ['Tipo de Envase', ...this.states.map(s => s.name)];

    // Construir las filas de datos
    const data = this.dataSource.map(row => {
      const rowData: any = {
        'Tipo de Envase': row.containerType.name
      };
      this.states.forEach(state => {
        rowData[state.name] = this.getTotalForState(row, state.id);
      });
      return rowData;
    });

    // Crear el worksheet
    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(data, { header: headers });

    // Ajustar el ancho de las columnas
    const columnWidths = headers.map(header => ({ wch: Math.max(header.length + 2, 15) }));
    worksheet['!cols'] = columnWidths;

    // Crear el workbook
    const workbook: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Tipo de Envase');

    // Generar nombre del archivo con fecha
    const date = new Date();
    const dateStr = `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}`;
    const fileName = `tipo_envase_${dateStr}.xlsx`;

    // Descargar el archivo
    XLSX.writeFile(workbook, fileName);
  }
}
